import { useEffect, useReducer, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import useCountries from '../../hooks/useCountries';

import {
  APPLICANT_TYPES,
  APPLICATION_FUNDING_TYPE,
  APPLICATION_MARTIAL_STATUS_TYPE,
  APPLICATION_TYPE,
  CorporateAccountType,
  IRA_APPLICATION_FUNDING_TYPES,
} from '../../util/constants';
import { findCountryByCode } from '../../util/helpers/countries';
import { useMenu } from '../../hooks/useMenu';
import { useApplicantData } from '../../hooks/useApplicantData';
import Paragraph from '../../components/Paragraph/Paragraph';
import AccountNameForm from '../../components/Forms/AccountNameForm';
import AddressForm from '../../components/Forms/AddressForm';
import AccountDobForm from '../../components/Forms/AccountDobForm';
import SsnForm from '../../components/Forms/SsnForm';
import IdentityCountryForm from '../../components/Forms/IdentityCountryForm';
import useStepPost from '../../hooks/useStepPost';
import useApplicationFundingData from '../../hooks/useApplicationFundingData';
import MaritalStatusForm from '../../components/Forms/MaritalStatusForm';
import { extractValue, hashedMask } from '../../util/helpers/ssnHelper';
import { parseEnumType } from '../../util/helpers/enumMappers';
import useBankApplicationFundingData from '../../hooks/useBankApplicationFundingData';
import {
  ACCOUNT_STEP_COMPLETE,
  ACCOUNT_STEP_VIEW,
  APPLICATION_FUNDED,
} from '../../util/gtmEvents';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import useGtmHook from '../../util/hooks/useGtmHook';
import { useMetalStreamData } from '../../hooks/useMetalStreamData';
import useIraApplicationFundingData from '../../hooks/useApplicationIraFundingData';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { Heading } from '../../components/Heading';
import { InformationContainer } from '../../components/ReviewElements';
import FundingReview from '../../components/Review/FundingReview';
import IraFundingReview from '../../components/Review/IraFundingReview';
import ReviewData from '../../components/Review/ReviewData';
import Name from '../../components/Name';
import AddressReviewFormat, {
  CountryName,
} from '../../components/AddressReviewFormat';
import Birthday from '../../components/Birthday';
import PhoneFormat from '../../components/Phone';
import Modal from '../../components/Modal';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import FundMetalReview from '../../components/Funding/FundMetalReview';
import FundModal from '../../components/Funding/FundModal';
import IraFundModal from '../../components/Funding/IraFundModal';
import RetirementBusinessNameForm from '../../components/Forms/RetirementBusinessNameForm';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE,
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  SALES_ASSISTED_QUERY_DATA,
} from '../../constants/sessionStorage';
import MoreInfo from './MoreInfo';
import PhoneNumberForm from '../../components/Forms/PhoneNumberForm';
import themeColors from '../../assets/styles/themeColors';
import SalesAssistedModal from '../../components/Modals/SalesAssistedModal/SalesAssistedModal';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { ReactComponent as InviteIcon } from '../../assets/images/svg/invite.svg';
import { pxToRem } from '../../assets/styles/helper';
import useApplicationUid from '../../hooks/useApplicationUid';
import { salesAssistedSendInviteUser } from '../../store/actions/salesAssisted/salesAsssistedActions';
import { APPLICATION_TYPE_IRA } from '../../constants/accountTypes';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import useCompanyName from '../../hooks/useCompanyName';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { getBackRoute } from '../../util/helpers/backNavigationHelpers';
import LastFourSsnForm from '../../components/Forms/LastFourSsnForm';
import MissingInfo from './MissingInfo';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 0 1.5rem 0 1.5rem;

  @media only screen and (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;
const IconContainer = styled.div`
  margin-right: 10px;
`;
const InviteIconStyled = styled(InviteIcon)`
  width: ${pxToRem(16)};
  height: ${pxToRem(16)};
  // stroke: ${({ color }) => color || themeColors.colorTextSecondary};
`;
const SalesAssistedButtonTitle = styled.div`
  display: flex;
  flex-direction: row;
`;

const reducer = (state, action) => ({
  ...state,
  ...action.payload,
});

const initialState = {
  isAddressOpen: false,
  isNameOpen: false,
  isSsnOpen: false,
  isDobOpen: false,
  isCountryOpen: false,
  isPhoneOpen: false,
  isMaritalOpen: false,
  isBusinessNameOpen: false,
  isSubmitSuccessful: false,
  isFundingOpen: false,
  isIraFundingOpen: false,
};

const Review = ({ isMetal }) => {
  const [
    {
      isAddressOpen,
      isNameOpen,
      isSsnOpen,
      isDobOpen,
      isCountryOpen,
      isPhoneOpen,
      isMaritalOpen,
      isBusinessNameOpen,
      isFundingOpen,
      isIraFundingOpen,
    },
    setModal,
  ] = useReducer(reducer, initialState);
  const { activeMenu, activeItem } = useMenu();
  const { t } = useTranslation();
  const { onSuccess } = useStepPost({ nextPath: activeItem?.nextPath });
  const ApplicantData = useApplicantData();
  const minor = useApplicantData(APPLICANT_TYPES.MINOR);
  const joint = useApplicantData(APPLICANT_TYPES.SECONDARY);
  const corporate = useApplicantData(APPLICANT_TYPES.BUSINESS);
  const countries = useCountries();
  const Application = useSelector(selectApplication);
  const isAssisted = Application?.IsAssisted;
  const CompanyName = useCompanyName();
  const { MetalStreamData } = useMetalStreamData();
  const { ApplicationFunding } = useApplicationFundingData();
  const { BankApplicationFunding } = useBankApplicationFundingData();
  const { IraApplicationFundingResponse } = useIraApplicationFundingData();
  const AccountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const dispatch = useDispatch();
  const { ApplicationType } = Application;
  const iraApplication =
    APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE_IRA;
  const [funding, setFunding] = useState({});
  const [iraFunding, setIraFunding] = useState({});
  const { gtmEvent } = useGtmHook();
  const [invitationSendModalOpen, setIsInvitationSendModalOpen] = useState(
    false,
  );
  const { ApplicationUid } = useApplicationUid();
  const [hasMissingInfo, setHasMissingInfo] = useState(true);
  const { BrokerName } = useSelector(selectSettings);
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);
  const IS_IRA = APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[3];
  const IS_UTMA = APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[5];

  useEffect(() => {
    if (
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday &&
      ApplicantData?.PersonAddress
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Review',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
        accountAddress: ApplicantData?.PersonAddress,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   if(BankApplicationFunding )
  // })

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Review',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  useEffect(() => {
    if (BankApplicationFunding) {
      setFunding(BankApplicationFunding);
      return;
    }

    if (IraApplicationFundingResponse) {
      setIraFunding(IraApplicationFundingResponse);
    }

    if (ApplicationFunding) {
      setFunding(ApplicationFunding);
      return;
    }

    setFunding({});
  }, [ApplicationFunding, BankApplicationFunding]); //eslint-disable-line

  const {
    PersonAddress,
    PersonBirthday,
    PersonCountry,
    PersonName,
    PersonSsn,
    PersonMaritalStatus,
    Phone,
  } = ApplicantData;

  const onSuccessInviteSend = () => {
    setIsInvitationSendModalOpen(true);
  };
  const onLooksGoodClick = () => {
    if (isSalesAssisted) {
      dispatch(
        salesAssistedSendInviteUser({
          data: {
            ApplicationUid,
          },
          onSuccess: onSuccessInviteSend,
          onError: () => console.log(error), //eslint-disable-line
        }),
      );
      return;
    }
    if (!isEmpty(funding)) {
      gtmEvent(APPLICATION_FUNDED, {
        event_label: 'registration',
        event_value: funding?.Amount,
        funding_type: parseEnumType(
          APPLICATION_FUNDING_TYPE,
          funding?.BankFundingType,
        ),
        application_type: APPLICATION_TYPE[ApplicationType],
      });
    }

    if (!isEmpty(iraFunding)) {
      gtmEvent(APPLICATION_FUNDED, {
        event_label: 'registration',
        event_value: IraApplicationFundingResponse?.Amount,
        funding_type: parseEnumType(
          IRA_APPLICATION_FUNDING_TYPES,
          IraApplicationFundingResponse.IRAFundingType,
        ),
        application_type: APPLICATION_TYPE[ApplicationType],
      });
    }

    if (isMetal) {
      gtmEvent(APPLICATION_FUNDED, {
        event_label: 'registration',
        event_value: MetalStreamData?.Amount,
        application_type: APPLICATION_TYPE[ApplicationType],
      });
    }

    onSuccess();
    handleGtmEvent();
  };

  const secondOwner = () => {
    if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[4]) {
      return joint;
    }
    if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[5]) {
      return minor;
    }
    if (
      APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[2] &&
      AccountType === CorporateAccountType[4]
    ) {
      return corporate;
    }
    return {};
  };

  const renderMainHeading = () => {
    if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[4]) {
      return t('reviewTitle.mainHeading.primaryHolder');
    }
    if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[5]) {
      return t('reviewTitle.mainHeading.custodian');
    }
    if (
      APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[2] &&
      AccountType === CorporateAccountType[4]
    ) {
      return t('reviewTitle.mainHeading.authorizedAgent');
    }
  };

  const renderSecondaryHeading = () => {
    if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[4]) {
      return t('reviewTitle.secondaryHeading.secondaryHolder');
    }
    if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[5]) {
      return t('reviewTitle.secondaryHeading.minor');
    }
    if (
      APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[2] &&
      AccountType === CorporateAccountType[4]
    ) {
      return t('reviewTitle.secondaryHeading.legalEntity');
    }
  };

  const isFundingAmountMissing = () => {
    if (funding) {
      if (funding.BankFundingType && !funding.Amount) {
        // setHasMissingInfo(true);
        return true;
      }
    }

    if (IraApplicationFundingResponse) {
      if (
        IraApplicationFundingResponse.IRAFundingType &&
        !IraApplicationFundingResponse.Amount
      ) {
        // setHasMissingInfo(true);
        return true;
      }
    }

    return false;
  };

  const isSsnMissing = () => {
    if (!isAssisted) {
      return false;
    }
    if (PersonCountry?.CountryIso3Code === 'USA' && !PersonSsn?.Ssn) {
      // setHasMissingInfo(true);
      return true;
    }

    return false;
  };

  const isButtonDisabled = () => {
    if (isSalesAssisted) {
      return false;
    }
    if (isFundingAmountMissing()) {
      return true;
    }

    if (hasMissingInfo && !isSalesAssisted) {
      return true;
    }

    if (
      joint?.PersonCountry?.CountryIso3Code === 'USA' &&
      isEmpty(joint?.PersonSsn.Ssn)
    ) {
      // setHasMissingInfo(true);
      return true;
    }

    if (
      minor &&
      minor?.PersonCountry?.CountryIso3Code === 'USA' &&
      isEmpty(minor?.PersonSsn.Ssn)
    ) {
      // setHasMissingInfo(true);
      return true;
    }

    return false;
  };

  const getSsnValue = () => {
    if (isSsnMissing()) {
      return <MissingInfo />;
    }

    if (PersonSsn?.Ssn) {
      if (PersonSsn.Ssn?.length > 4) {
        return hashedMask(extractValue(PersonSsn?.Ssn));
      }

      return `XXX-XX-${PersonSsn?.Ssn}`;
    }

    return 'N/A';
  };

  const isNameMissing = () => {
    if (PersonName?.FirstName === '' && PersonName?.LastName === '') {
      return true;
    }
    return false;
  };

  const isTrustNameMissing = () => {
    const secondOwnerValue = secondOwner();
    const { Name: TrustName } = secondOwnerValue;
    if (
      APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[2] &&
      AccountType === CorporateAccountType[4] &&
      !TrustName
    ) {
      return true;
    }
    return false;
  };

  const isCompanyNameMissing = () => {
    if (
      iraApplication &&
      (AccountType === 'SEP' || AccountType === 'SIMPLE') &&
      !CompanyName
    ) {
      return true;
    }
    return false;
  };

  const isAddressMissing = () => {
    if (
      PersonAddress?.CountryIso3Code === '' &&
      PersonAddress?.City === '' &&
      PersonAddress?.State === ''
    ) {
      return true;
    }
    return false;
  };

  const isBirthdayMissing = () => {
    if (!PersonBirthday?.DateOfBirth) {
      return true;
    }
    return false;
  };

  const isPhoneMissing = () => {
    if (
      !Phone?.Iso3CountryCode &&
      !Phone?.LocalNumber &&
      !Phone?.StateCode &&
      !Phone?.AreaCode
    ) {
      return true;
    }
    return false;
  };
  useEffect(() => {
    if (!isEmpty(ApplicantData)) {
      if (!isAssisted) {
        setHasMissingInfo(false);
        return;
      }
      if (
        isNameMissing() ||
        isSsnMissing() ||
        isAddressMissing() ||
        isBirthdayMissing() ||
        isPhoneMissing() ||
        isCompanyNameMissing() ||
        isTrustNameMissing()
      ) {
        setHasMissingInfo(true);
        return;
      }
      setHasMissingInfo(false);
    }
  }, [ApplicantData]); //eslint-disable-line

  useEffect(() => {
    document.body.style.overflow = 'unset';
  }, []); // eslint-disable-line

  const salesAssistedButtonTitle = () => (
    <SalesAssistedButtonTitle>
      <IconContainer>
        <InviteIconStyled />
      </IconContainer>
      {t('impersonateRegistration')}
    </SalesAssistedButtonTitle>
  );

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      hasMissingInfo={hasMissingInfo && !isSalesAssisted}
      previousPath={
        IS_IRA
          ? activeItem.previousPath
          : getBackRoute(
              activeItem,
              ApplicationType,
              funding,
              IraApplicationFundingResponse,
            )
      }
      setEventType={setEventType}
      eventType={eventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <Helmet>
        <title>
          {t('titleTagReview', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Heading mb={12}>{t('standardReview.title')}</Heading>
      <Paragraph mb={24} color={themeColors.colorTextXLLight}>
        {t('standardReview.subtitle')}
      </Paragraph>
      <InformationContainer>
        <Paragraph ml={24} mb={24} variant="large">
          {renderMainHeading()}
        </Paragraph>
        <Grid>
          {iraApplication &&
            (AccountType === 'SEP' || AccountType === 'SIMPLE') && (
              <ReviewData
                title={t('verifyReview.businessNameLabel')}
                onEdit={() =>
                  setModal({ payload: { isBusinessNameOpen: true } })
                }
                value={!CompanyName ? <MissingInfo /> : CompanyName}
              />
            )}
          {!isEmpty(PersonName) && (
            <ReviewData
              title={t('standardReview.yourName')}
              onEdit={() => setModal({ payload: { isNameOpen: true } })}
            >
              <Name {...PersonName} isSalesAssisted={isSalesAssisted} />
            </ReviewData>
          )}
          {!isEmpty(PersonAddress) && (
            <ReviewData
              title={t('standardReview.address')}
              onEdit={() => setModal({ payload: { isAddressOpen: true } })}
            >
              <AddressReviewFormat
                {...PersonAddress}
                isSalesAssisted={isSalesAssisted}
              />
            </ReviewData>
          )}
          {!isEmpty(PersonBirthday) && (
            <ReviewData
              title={t('standardReview.yourBirthday')}
              onEdit={() => setModal({ payload: { isDobOpen: true } })}
            >
              <Birthday {...PersonBirthday} isSalesAssisted={isSalesAssisted} />
            </ReviewData>
          )}
          {PersonCountry?.CountryIso3Code === 'USA' && (
            <ReviewData
              title={t('standardReview.yourSsn')}
              onEdit={() => setModal({ payload: { isSsnOpen: true } })}
              value={getSsnValue()}
            />
          )}

          {PersonCountry?.CountryIso3Code && (
            <ReviewData
              title={t('standardReview.yourCitizenship')}
              onEdit={() => setModal({ payload: { isCountryOpen: true } })}
            >
              <Paragraph color={themeColors.colorTextXLLight} variant="normal">
                <CountryName>
                  {findCountryByCode(countries, PersonCountry?.CountryIso3Code)}
                </CountryName>
              </Paragraph>
            </ReviewData>
          )}

          <ReviewData
            title={t('standardReview.yourPhone')}
            onEdit={() => setModal({ payload: { isPhoneOpen: true } })}
          >
            <PhoneFormat Phone={Phone} isSalesAssisted={isSalesAssisted} />
          </ReviewData>

          {!!PersonMaritalStatus?.MaritalStatus && (
            <ReviewData
              title={t('standardReview.maritalStatus')}
              onEdit={() => setModal({ payload: { isMaritalOpen: true } })}
            >
              <Paragraph color={themeColors.colorTextXLLight} variant="normal">
                {parseEnumType(
                  APPLICATION_MARTIAL_STATUS_TYPE,
                  PersonMaritalStatus?.MaritalStatus,
                )}
              </Paragraph>
            </ReviewData>
          )}
        </Grid>
        <Paragraph ml={24} mb={24} mt={48} variant="large">
          {renderSecondaryHeading()}
        </Paragraph>
        {secondOwner() && (
          <MoreInfo
            secondOwner={secondOwner()}
            isSalesAssisted={isSalesAssisted}
            setHasMissingInfo={setHasMissingInfo}
          />
        )}
        {isMetal && <FundMetalReview mb={24} />}
        {iraApplication && (
          <IraFundingReview
            mb={24}
            onEdit={() => setModal({ payload: { isIraFundingOpen: true } })}
            funding={IraApplicationFundingResponse}
            missingInformation={isFundingAmountMissing()}
          />
        )}
        {!iraApplication && (
          <FundingReview
            mb={24}
            onEdit={() => setModal({ payload: { isFundingOpen: true } })}
            funding={funding}
            missingInformation={isFundingAmountMissing()}
          />
        )}
      </InformationContainer>
      <OnBoardingCta
        title={isSalesAssisted ? salesAssistedButtonTitle() : t('looksGood')}
        disabled={isButtonDisabled()}
        onClick={onLooksGoodClick}
      />

      <Modal
        isOpen={isNameOpen}
        onClose={() => {
          setModal({ payload: { isNameOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourName')}
      >
        <AccountNameForm
          onModalClose={() => {
            setModal({ payload: { isNameOpen: false } });
            setEventType('NoForm');
          }}
          applicantType={APPLICANT_TYPES.MAIN}
          formId="name_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isAddressOpen}
        onClose={() => {
          setModal({ payload: { isAddressOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.address')}
      >
        <AddressForm
          applicantType={APPLICANT_TYPES.MAIN}
          onModalClose={() => {
            setModal({ payload: { isAddressOpen: false } });
            setEventType('NoForm');
          }}
          formId="address_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isDobOpen}
        onClose={() => {
          setModal({ payload: { isDobOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourBirthday')}
      >
        <AccountDobForm
          applicantType={APPLICANT_TYPES.MAIN}
          onModalClose={() => {
            setModal({ payload: { isDobOpen: false } });
            setEventType('NoForm');
          }}
          formId="dob_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isSsnOpen}
        onClose={() => {
          setModal({ payload: { isSsnOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourSsn')}
      >
        {(IS_IRA && !IS_UTMA) || PersonSsn.Ssn?.length > 4 ? (
          <SsnForm
            applicantType={APPLICANT_TYPES.MAIN}
            onModalClose={() => {
              setModal({ payload: { isSsnOpen: false } });
              setEventType('NoForm');
            }}
            formId="ssn_form"
            setEventType={setEventType}
            eventType={eventType}
          />
        ) : (
          <LastFourSsnForm
            applicantType={APPLICANT_TYPES.MAIN}
            onModalClose={() => {
              setModal({ payload: { isSsnOpen: false } });
              setEventType('NoForm');
            }}
            formId="ssn_form"
            setEventType={setEventType}
            eventType={eventType}
          />
        )}
      </Modal>
      <Modal
        isOpen={isCountryOpen}
        onClose={() => {
          setModal({ payload: { isCountryOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourCitizenship')}
      >
        <IdentityCountryForm
          applicantType={APPLICANT_TYPES.MAIN}
          onModalClose={() => {
            setModal({ payload: { isCountryOpen: false } });
            setEventType('NoForm');
          }}
          formId="identity_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isPhoneOpen}
        onClose={() => {
          setModal({ payload: { isPhoneOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourPhone')}
      >
        <PhoneNumberForm
          applicantType={APPLICANT_TYPES.MAIN}
          onModalClose={() => {
            setModal({ payload: { isPhoneOpen: false } });
            setEventType('NoForm');
          }}
          formId="phone_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isMaritalOpen}
        onClose={() => {
          setModal({ payload: { isMaritalOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.maritalStatus')}
      >
        <MaritalStatusForm
          onModalClose={() => {
            setModal({ payload: { isMaritalOpen: false } });
            setEventType('NoForm');
          }}
        />
      </Modal>
      <Modal
        isOpen={isBusinessNameOpen}
        onClose={() => {
          setModal({ payload: { isBusinessNameOpen: false } });
          setEventType('NoForm');
        }}
        label={t('verifyReview.businessNameLabel')}
      >
        <RetirementBusinessNameForm
          formId="business_name_form"
          setEventType={setEventType}
          eventType={eventType}
          onModalClose={() => {
            setModal({ payload: { isBusinessNameOpen: false } });
            setEventType('NoForm');
          }}
        />
      </Modal>
      <FundModal
        isOpen={isFundingOpen}
        onClose={() => {
          setModal({ payload: { isFundingOpen: false } });
          setEventType('NoForm');
        }}
        onModalClose={() => {
          setModal({ payload: { isFundingOpen: false } });
          setEventType('NoForm');
        }}
        funding={funding}
      />
      <IraFundModal
        isOpen={isIraFundingOpen}
        onClose={() => {
          setModal({ payload: { isIraFundingOpen: false } });
          setEventType('NoForm');
        }}
        onModalClose={() => {
          setModal({ payload: { isIraFundingOpen: false } });
          setEventType('NoForm');
        }}
      />
      <SalesAssistedModal
        isOpen={invitationSendModalOpen}
        onClose={() => {
          setIsInvitationSendModalOpen(false);
          removeFromSessionStorage(IS_SALES_ASSESSTED_REGISTRATION_FLOW);
          removeFromSessionStorage(SALES_ASSISTED_QUERY_DATA);
          window.location.replace(
            'https://gbi-pilot.bullioninternational.info/Backoffice/DraftApplications',
          );
        }}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
        />
      )}
    </OnBoardingContainer>
  );
};

Review.propTypes = {
  isMetal: PropTypes.bool,
};

Review.defaultProps = {
  isMetal: false,
};

export default Review;
